import React, { useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert
} from '@mui/material';
import { Payment } from '@mui/icons-material';

/**
 * SimplePaymentAmountDialog - A reusable dialog for entering payment amounts
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Called when dialog should close
 * @param {object} patient - Patient object with name, id, and balance info
 * @param {string|number} amount - Current payment amount value
 * @param {function} onAmountChange - Called when amount changes
 * @param {function} onProceed - Called when user proceeds to payment
 * @param {function} formatCurrency - Function to format currency values
 * @param {string} title - Optional custom title (default: "Enter Payment Amount")
 * @param {string} balanceLabel - Optional custom balance label (default: "Outstanding Balance")
 */

const SimplePaymentAmountDialog = ({
  open,
  onClose,
  patient,
  amount,
  onAmountChange,
  onProceed,
  formatCurrency = (val) => `GH₵${parseFloat(val || 0).toFixed(2)}`,
  title = "Enter Payment Amount",
  balanceLabel = "Outstanding Balance"
}) => {
  
  const inputRef = useRef(null);
  const patientBalance = patient?.balanceDue || patient?.balance || 0;
  const patientName = patient?.name || patient?.fullName || 'Unknown';
  const patientId = patient?.id || 'N/A';

  // Keep focus on input after each change
  useEffect(() => {
    if (open && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [open, amount]);

  const handleProceed = () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }
    if (parseFloat(amount) > patientBalance) {
      return;
    }
    onProceed();
  };

  const handleClose = () => {
    onClose();
  };

  const isValidAmount = amount && parseFloat(amount) > 0 && parseFloat(amount) <= patientBalance;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle sx={{ 
        bgcolor: '#4caf50',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Payment sx={{ mr: 1 }} />
          {title}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Patient: <strong>{patientName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Patient ID: <strong>{patientId}</strong>
          </Typography>
          <Typography variant="body2" color="error" gutterBottom sx={{ mb: 2 }}>
            {balanceLabel}: <strong>{formatCurrency(patientBalance)}</strong>
          </Typography>
          
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={amount || ''}
            onChange={(e) => {
              onAmountChange(e.target.value);
              // Re-focus immediately after change
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }, 0);
            }}
            inputRef={inputRef}
            sx={{ mt: 2, mb: 2 }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>GH₵</Typography>
            }}
            helperText={`Enter amount to pay (Max: ${formatCurrency(patientBalance)})`}
            inputProps={{
              min: 0,
              max: patientBalance,
              step: 0.01
            }}
            autoFocus
            autoComplete="off"
          />

          {amount && parseFloat(amount) > 0 && parseFloat(amount) <= patientBalance && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Payment Amount:</strong> {formatCurrency(parseFloat(amount))}<br />
                <strong>Remaining Balance:</strong> {formatCurrency(patientBalance - parseFloat(amount))}
              </Typography>
            </Alert>
          )}

          {amount && parseFloat(amount) > patientBalance && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Payment amount cannot exceed the outstanding balance
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleProceed}
          disabled={!isValidAmount}
          sx={{ 
            bgcolor: '#4caf50',
            '&:hover': {
              bgcolor: '#45a049'
            }
          }}
        >
          Proceed to Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimplePaymentAmountDialog;